import { Alert, Box, Button, Col, Heading, Icon, Row, Spinner, TabContainer, TabSection, } from '@digitools/honest-react';
import axios from 'axios';
import FileSaver from 'file-saver';
import React, { useCallback, useEffect, useState } from 'react';
import { TEALIUM_EVENT, TEALIUM_PAGE_VIEW } from 'src/constants/tealiumConstants';
import useAuthentication from 'src/hooks/useAuthentication';
import useTealium from 'src/hooks/useTealium';
import { RecordType } from 'src/types/IEmployerResource';
import { globalPhoneNumberFormat } from 'src/utils/validationUtils';
import { isLfgMlpUser } from 'src/utils/userUtils';
import { isEmployerAdmin } from 'src/utils/userRoleUtils';
import LfgBaseLogo from 'src/assets/lfg_base_logo.svg';
import UserAccessDetails from '../../permissions/UserAccessDetails';
import CopyUserModal from '../components/CopyUserModal';
import EmployerPermissions from '../components/EmployerPermissions';
import RemoveUserModal from '../components/RemoveUserModal';
import UserSearch from '../components/UserSearch';
import styled from 'styled-components';
export const StyledUserPermissionButton = styled(Button) `
  white-space: normal;
  padding: 0;
  text-align: left;
  line-height: var(--mantine-line-height);
  height: 0;
`;
// "Logs in from lincolnfinancial.com" indicator: static layout + typography moved
// off inline styles into styled components (CX-3873 review N3).
const StyledLfgMlpIndicator = styled('div') `
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  margin-top: 1rem;
`;
const StyledLfgMlpIndicatorText = styled('span') `
  font-family: Roboto;
  font-size: 1rem;
  font-style: italic;
  font-weight: 400;
`;
const UserPermissionsContainer = () => {
    const { authHeader, user } = useAuthentication();
    const { trackEvent, trackView } = useTealium();
    const [isPageLoading, setIsPageLoading] = useState(true);
    const [isReportGenerating, setIsReportGenerating] = useState(false);
    const [allUsers, setAllUsers] = useState([]);
    // Employer Admin accounts cannot be modified, so they must not appear as
    // copy-from / copy-to targets in the Copy Permissions modal (US-006). They
    // remain in `allUsers` so the user list (US-005) still shows them.
    const copyEligibleUsers = allUsers.filter(u => !isEmployerAdmin(u.roleNames));
    const [isSearchString, setIsSearchString] = useState(false);
    const [results, setResults] = useState([]);
    const [selectedUser, setSelectedUser] = useState(undefined);
    const [showAllUsersState, setShowAllUsersState] = useState(true);
    const [errorMessage, setErrorMessage] = useState(undefined);
    const [directories, setDirectories] = useState([]);
    const [modifiedDirectoryAccessIds, setModifiedDirectoryAccessIds] = useState([]);
    const [modifiedEmployerDirectoryAccessIds, setModifiedEmployerDirectoryAccessIds] = useState([]);
    const [isAlert, setIsAlert] = useState(false);
    const [userData, setUserData] = useState(undefined);
    const [configData, setConfigData] = useState(undefined);
    const [permissionData, setPermissionData] = useState(undefined);
    const [isEmployerLoaded, setIsEmployerLoaded] = useState(false);
    const [copyPermissionsStatus, setCopyPermissionsStatus] = useState(undefined);
    const [copyPermissionsStatusMessageFirstName, setCopyPermissionsStatusMessageFirstName] = useState('');
    const [copyPermissionsStatusMessageLastName, setCopyPermissionsStatusMessageLastName] = useState('');
    const getHeader = useCallback(() => ({
        headers: {
            'Content-Type': 'application/json',
            Authorization: authHeader(),
        },
    }), [authHeader]);
    const getAllEmployerUsers = useCallback(async () => {
        try {
            const response = await axios.get(`/user-service/services/v1/users/employers?active=true`, {
                headers: {
                    Authorization: authHeader(),
                },
            });
            setIsPageLoading(false);
            setAllUsers(response.data);
            setIsAlert(response.data.length === 0);
        }
        catch (error) {
            setIsPageLoading(false);
            setErrorMessage(`We're sorry, we are unable to retrieve users at this time. If this issue persist please contact Technical Support`);
        }
    }, [authHeader]);
    const getCustomerConfigData = useCallback(async () => {
        try {
            const configResponse = await axios.get(`/mlp-ui-service/services/v1/customer/employer/configuration`, getHeader());
            setConfigData(configResponse.data);
        }
        catch (error) {
            setErrorMessage(`Error getting customer config information`);
        }
    }, [getHeader]);
    const getCustomerAppPermissions = useCallback(async () => {
        const customerId = user ? user.customerId : '';
        try {
            const customerPermissionResponse = await axios.get(`/auth-service/services/v1/customer/${customerId}/permissions`, getHeader());
            setPermissionData(customerPermissionResponse.data);
        }
        catch (error) {
            setErrorMessage(`Error getting customer Permissions`);
        }
    }, [user, getHeader]);
    useEffect(() => {
        getAllEmployerUsers();
        getCustomerConfigData();
        getCustomerAppPermissions();
    }, []);
    // Fire the User Access page view on arrival. page_l4 (employer/employee view), page_plan_code,
    // user_id, user_role, and company_name are auto-populated from the user context by trackView.
    useEffect(() => {
        trackView({
            page_l3: TEALIUM_PAGE_VIEW.PAGE_L3.USER_ACCESS,
        });
    }, []);
    const getAllDirectories = useCallback(async (userForDirectories) => {
        const customerId = user ? user.customerId : '';
        const userId = userForDirectories ? userForDirectories.userId : '';
        const requestData = {
            customerId: customerId,
            userId: userId,
        };
        setDirectories([]);
        try {
            const response = await axios.post(`/document-service/reports/directories`, requestData, {
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: authHeader(),
                },
            });
            const employerResourceResponse = await axios.post(`/document-service/employer-resources/directories`, requestData, {
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: authHeader(),
                },
            });
            setDirectories([...response.data, ...employerResourceResponse.data]);
        }
        catch (error) {
            setErrorMessage(`Error getting report directories for customer ${customerId}`);
        }
    }, [user, authHeader]);
    const getEmployerAccess = useCallback(async (userForAccess) => {
        setUserData(undefined);
        setIsEmployerLoaded(false);
        const userId = userForAccess ? userForAccess.userId : '';
        try {
            const response = await axios.get(`/user-service/services/v1/users/employers/${userId}`, getHeader());
            setUserData(response.data);
        }
        catch (error) {
            setErrorMessage(`Error getting user information for ${userId}`);
        }
        setIsEmployerLoaded(true);
    }, [getHeader]);
    const handleSelectedUser = useCallback(async (userToSelect) => {
        trackEvent({
            event_action: TEALIUM_EVENT.EVENT_ACTION.LINK,
            event_type: TEALIUM_EVENT.EVENT_TYPE.USER_PERMISSIONS,
            event_name: 'Employer user selected',
        });
        setIsSearchString(false);
        setSelectedUser(userToSelect);
        setShowAllUsersState(false);
        setCopyPermissionsStatus(false);
        await getEmployerAccess(userToSelect);
        await getAllDirectories(userToSelect);
    }, [trackEvent, getEmployerAccess, getAllDirectories]);
    const removeUser = useCallback(async (userToRemove) => {
        try {
            setShowAllUsersState(false);
            const requestBody = {
                userId: userToRemove.userId,
                removedEmployerAccess: true,
            };
            await axios.patch(`/user-service/services/v1/users/employers`, requestBody, {
                headers: { Authorization: authHeader() },
            });
            await getAllEmployerUsers();
            showAllUsersCall();
        }
        catch (error) {
            setIsPageLoading(false);
            setErrorMessage(`Error removing user`);
        }
    }, [authHeader, getAllEmployerUsers]);
    const showAllUsersCall = useCallback(() => {
        trackEvent({
            event_action: TEALIUM_EVENT.EVENT_ACTION.LINK,
            event_type: TEALIUM_EVENT.EVENT_TYPE.USER_PERMISSIONS,
            event_name: 'Show all users',
        });
        setShowAllUsersState(true);
        setSelectedUser(undefined);
        setIsSearchString(false);
        setResults([]);
        setIsAlert(allUsers.length === 0);
    }, [trackEvent, allUsers]);
    const closeAlert = useCallback(() => {
        trackEvent({
            event_action: TEALIUM_EVENT.EVENT_ACTION.LINK,
            event_type: TEALIUM_EVENT.EVENT_TYPE.USER_PERMISSIONS,
            event_name: 'Close alert',
        });
        setErrorMessage(undefined);
        setIsAlert(false);
    }, [trackEvent]);
    const onCheckBoxChange = useCallback(async (value, fileItem) => {
        const resource = fileItem;
        if (resource.recordType && resource.recordType === RecordType.CUSTOMER) {
            setModifiedEmployerDirectoryAccessIds(prev => {
                if (prev.includes(resource.id)) {
                    return prev.filter(id => id !== resource.id);
                }
                else {
                    return [...prev, resource.id];
                }
            });
        }
        else {
            setModifiedDirectoryAccessIds(prev => {
                if (prev.includes(fileItem.id)) {
                    return prev.filter(id => id !== fileItem.id);
                }
                else {
                    return [...prev, fileItem.id];
                }
            });
        }
    }, []);
    const cancelUserFolderPermissions = useCallback(async () => {
        trackEvent({
            event_action: TEALIUM_EVENT.EVENT_ACTION.LINK,
            event_type: TEALIUM_EVENT.EVENT_TYPE.USER_PERMISSIONS,
            event_name: 'Undo User Folder Permissions',
        });
        setModifiedDirectoryAccessIds([]);
        setModifiedEmployerDirectoryAccessIds([]);
        getAllDirectories(selectedUser);
    }, [trackEvent, getAllDirectories, selectedUser]);
    const saveUserFolderPermissions = useCallback(async () => {
        try {
            trackEvent({
                event_action: TEALIUM_EVENT.EVENT_ACTION.LINK,
                event_type: TEALIUM_EVENT.EVENT_TYPE.USER_PERMISSIONS,
                event_name: 'Save User Folder Permissions',
            });
            const userId = selectedUser.userId;
            const reportsRequestData = {
                userId: userId,
                folderIds: modifiedDirectoryAccessIds,
            };
            await axios.put(`/document-service/reports/permissions`, reportsRequestData, {
                headers: {
                    Authorization: authHeader(),
                },
            });
            setModifiedDirectoryAccessIds([]);
            const erResourceRequestData = {
                userId: userId,
                folderIds: modifiedEmployerDirectoryAccessIds,
            };
            await axios.put(`/document-service/employer-resources/permissions`, erResourceRequestData, {
                headers: {
                    Authorization: authHeader(),
                },
            });
            setModifiedEmployerDirectoryAccessIds([]);
        }
        catch (error) {
            setErrorMessage('Error saving user permissions');
        }
        setIsAlert(true);
    }, [trackEvent, selectedUser, modifiedDirectoryAccessIds, modifiedEmployerDirectoryAccessIds, authHeader]);
    const handleSetCopyPermissionsState = useCallback(async (success, firstName, lastName, newSelectedUser) => {
        handleSelectedUser(newSelectedUser);
        setCopyPermissionsStatus(success);
        setCopyPermissionsStatusMessageFirstName(firstName);
        setCopyPermissionsStatusMessageLastName(lastName);
    }, [handleSelectedUser]);
    const generateUserReport = useCallback(async (forIndividualUser) => {
        trackEvent({
            event_action: TEALIUM_EVENT.EVENT_ACTION.BUTTON,
            event_type: TEALIUM_EVENT.EVENT_TYPE.USER_PERMISSIONS,
            event_name: 'User Report Generated',
        });
        setIsReportGenerating(true);
        let url = `/user-service/services/v1/user-reports/customer/${user.customerId}/excel`;
        if (forIndividualUser) {
            url = url + `?loginIds=${selectedUser.userId}`;
        }
        try {
            const response = await axios.get(url, {
                headers: {
                    Authorization: authHeader(),
                },
                responseType: 'arraybuffer',
            });
            const contentDispositionHeader = response.headers['content-disposition'];
            const contentType = response.headers['content-disposition'];
            const filename = contentDispositionHeader?.split(';')[1]?.split('filename')[1]?.split('=')[1]?.trim();
            const strippedFilename = JSON.parse(filename);
            const blob = new Blob([response.data], { type: contentType });
            FileSaver.saveAs(blob, strippedFilename);
        }
        catch (error) {
            setErrorMessage(`Error generating user report`);
        }
        setIsReportGenerating(false);
    }, [trackEvent, user, selectedUser, authHeader]);
    const renderSelectedUserInfo = () => {
        return isEmployerLoaded && (<Col md={3} lg={3}>
          <Box type='header' color={'grayLightest'} id='name' aria-labelledby='name'>
            {selectedUser && `${selectedUser.firstName} ${selectedUser.lastName}`}
          </Box>
          <Box style={{ overflowWrap: 'break-word' }}>
            <div id='EmailAddressHeader' aria-labelledby='EmailAddressHeader'>
              <b>Email Address</b>
            </div>
            <div id='EmailAddress' aria-labelledby='EmailAddress'>
              {userData && userData.email}
            </div>
            {selectedUser && isLfgMlpUser(selectedUser.userId) && (<StyledLfgMlpIndicator data-testid='lfgMlpIndicator'>
                <LfgBaseLogo aria-labelledby='lfg-mlp-indicator-text' style={{ flexShrink: 0, width: '27px', height: '27px' }}/>
                <StyledLfgMlpIndicatorText id='lfg-mlp-indicator-text'>
                  {'Logs in from lincolnfinancial.com'}
                </StyledLfgMlpIndicatorText>
              </StyledLfgMlpIndicator>)}
            <br />
            <div id='PhoneNumberHeader' aria-labelledby='PhoneNumberHeader'>
              <b>Phone Number</b>
            </div>
            <div id='PhoneNumber' aria-labelledby='PhoneNumber'>
              {userData && globalPhoneNumberFormat(userData.phoneNumber)}
            </div>
          </Box>
          {!user?.ssoUser &&
                <Box className={'mt-0 p-3'}>
                <RemoveUserModal removeSelectUser={removeUser} user={selectedUser}/>
              </Box>}
          <Box className={'mt-0 p-3'}>
            <StyledUserPermissionButton id={'download-user-report'} data-testid='openModal' aria-label='Download user report' onClick={() => (isReportGenerating ? null : generateUserReport(true))}>
              <b>
                <Icon name={'download'} color={'accent1Light'} id='download-user-report' size={'xs'}/> Download user
                report
              </b>
            </StyledUserPermissionButton>
          </Box>
          {selectedUser &&
                <Box className={'mt-0 pl-3 pr-2 pt-3 pb-3'}>
                <CopyUserModal user={selectedUser} allUsers={copyEligibleUsers} setCopyPermissionsState={handleSetCopyPermissionsState} selectedUser={selectedUser} showAllUsers={showAllUsersCall} changeSelectedUser={handleSelectedUser}/>
              </Box>}
        </Col>);
    };
    const employerDetails = isEmployerLoaded ? (<EmployerPermissions userInfo={userData} customerConfig={configData} permissions={permissionData} onCancel={() => getEmployerAccess(selectedUser)}/>) : (<Spinner id='user-permisions-spinner1'/>);
    const unSavedFolderChangesAlert = modifiedDirectoryAccessIds.length > 0 || modifiedEmployerDirectoryAccessIds.length > 0 ? (<Alert type={'warning'} className={'mt-3'}>
            <strong>Attention!</strong>&nbsp;You have unsaved changes.
          </Alert>) : (<></>);
    const userDetails = selectedUser ? (<>
        <Row data-public={true}>
          {renderSelectedUserInfo()}
          <Col md={9} lg={9} className={'mb-2'}>
            <Heading elemType={'h3'} elemStyle={'h4'}>Permissions</Heading>
            <TabContainer>
              <TabSection name={'Employer'}>{employerDetails}</TabSection>
              <TabSection name={'Folders'}>
                {unSavedFolderChangesAlert}
                <UserAccessDetails closeAlert={closeAlert} errorMessage={errorMessage} user={selectedUser} directories={directories} onCheckboxChange={onCheckBoxChange} saveUserPermissions={saveUserFolderPermissions} isAlert={isAlert} cancelUserPermissions={cancelUserFolderPermissions}/>
              </TabSection>
            </TabContainer>
          </Col>
        </Row>
      </>) : (<></>);
    const errorGettingUsersAlert = errorMessage ? (<Alert type='error' closeable={true} onClose={closeAlert} data-public={true}>
        {errorMessage}
      </Alert>) : (<></>);
    if (isPageLoading) {
        return <Spinner id='user-permissions-spinner2'/>;
    }
    return (<>
        <br />
        {errorGettingUsersAlert}
        <Row data-public={true}>
          <Col xs={12} md={6}>
            <Heading elemType='h1' id='UserAccessHeader' aria-labelledby='UserAccessHeader'>
              User Access
            </Heading>
          </Col>
        </Row>

        <div style={{ display: 'flex' }} className={'mb-3 mt-1'} data-public={true}>
          <Heading elemType={'h2'} elemStyle='h4' id='UserAccessMessage' aria-labelledby='UserAccessMessage' style={{ marginTop: '1rem' }}>
            Search for a user to modify or customize their permissions.
          </Heading>
          <Button id={'download-all-users-report'} style={{ marginLeft: 'auto', marginRight: '0rem' }} data-testid='openModal' aria-label='Download all users report' onClick={() => (!isReportGenerating ? generateUserReport(false) : null)}>
            <b>
              <Icon name={'download'} color={'accent1Light'} id='icon-download-all-users-report' size={'xs'}/>{' '}
              {isReportGenerating ? 'Generating Report...' : 'Download report'}
            </b>
          </Button>
        </div>
        <UserSearch selectedUser={selectedUser} users={allUsers} showAllIndicator={showAllUsersState} showAllUsers={showAllUsersCall} handleSelectUser={handleSelectedUser} removeUser={removeUser} showCopyPermissionsTable={false}/>
        <Row> {copyPermissionsStatus && (<Alert style={{ width: '100%' }} type='success' closeable={true} onClose={closeAlert} data-public={true}>
              Success! Permissions and folders from <b> {copyPermissionsStatusMessageFirstName} {copyPermissionsStatusMessageLastName}</b> have been applied and saved to this user.
            </Alert>)}</Row>
        {userDetails}
      </>);
};
export default UserPermissionsContainer;
